/**
 * 답례품 스토어 "미리보기" 모드 (관리자가 상조별 판매 화면을 확인하는 용도).
 *
 * 스토어는 전부 bugoId 기준으로 동작하므로(상품 목록도 by-bugo), 미리보기는
 * 해당 상조의 실제 부고 하나를 빌려 `/gift-store?bugoId=…&preview=1`로 연다.
 * 빌린 부고는 실제 상가이므로 미리보기 중에는
 * - 조회 이벤트 로그(store_view·item_view)를 남기지 않고 (전환 분석 오염 방지)
 * - 발인+30일 온라인 주문 마감 게이트를 우회하고
 * - 결제 화면 진입을 막는다 (남의 상가로 주문이 귀속되는 사고 방지)
 *
 * 단, 상조에 "테스트 상가"(bugoBrand.giftTestBugo)가 지정돼 있으면 그 부고로 열고
 * 결제까지 허용한다(test=true). 실제 상가가 아니라 주문이 귀속돼도 무해하기 때문.
 * 조회 로그는 이 경우에도 남기지 않는다.
 *
 * sessionStorage를 쓰는 이유: 스토어 안에서 이동하면 링크가 bugoId만 들고 다녀
 * `preview=1`이 떨어져 나가기 때문. 탭 단위라 다른 탭의 실제 스토어엔 영향이 없다.
 * 표시는 게이트가 관리자 로그인 상태에서만 남긴다(일반 고객이 preview=1을 붙여도 무시).
 */

const PREVIEW_KEY = 'giftStorePreview';

export const GIFT_STORE_PREVIEW_PARAM = 'preview';

interface GiftStorePreviewContext {
  bugoId: string;
  brandName?: string;
  //* 상조에 지정된 테스트 상가로 연 미리보기 — 결제 테스트 허용
  test?: boolean;
}

export const buildGiftStorePreviewUrl = (
  bugoId: string,
  brandName?: string,
  test = false,
): string => {
  const params = new URLSearchParams({ bugoId, [GIFT_STORE_PREVIEW_PARAM]: '1' });
  if (brandName) params.set('previewBrand', brandName);
  if (test) params.set('previewTest', '1');
  return `/gift-store?${params.toString()}`;
};

export const markGiftStorePreview = (
  bugoId: string,
  brandName?: string,
  test = false,
): void => {
  try {
    const value: GiftStorePreviewContext = { bugoId, brandName, test };
    sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(value));
  } catch {
    // 저장소 차단 환경 — 미리보기 표시만 못 남길 뿐 스토어는 정상 동작
  }
};

/** 활성 미리보기 컨텍스트. bugoId를 주면 해당 부고와 일치할 때만 반환한다. */
export const getGiftStorePreview = (bugoId?: string | null): GiftStorePreviewContext | null => {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw) as GiftStorePreviewContext;
    if (!ctx?.bugoId) return null;
    if (bugoId && ctx.bugoId !== bugoId) return null;
    return ctx;
  } catch {
    return null;
  }
};

export const clearGiftStorePreview = (): void => {
  try {
    sessionStorage.removeItem(PREVIEW_KEY);
  } catch {
    // noop
  }
};
